/**
 * Catalog dataset versioning (PLAN §6).
 *
 * The catalog is a versioned product asset, not user data. Recipes snapshot
 * the ingredient values they use *and* record the version those values came
 * from, so a recipe brewed today reads identically after the catalog is
 * updated.
 */

/** The collections `collection-counts.json` counts, and this file mirrors. */
type CatalogCollection = 'fermentables' | 'hops' | 'yeasts' | 'miscs' | 'equipment' | 'styles';

/**
 * The shipped dataset version, `YYYY.MM.PATCH`.
 *
 * Date-based rather than semver because there is no such thing as a breaking
 * change to a row of malt specs — what matters is *when* the snapshot was
 * taken. Bump the patch for corrections and additions inside a month; roll to
 * a new `YYYY.MM.0` on the first release of a new month.
 *
 * Schema changes are versioned by the package, not by this constant.
 */
export const CATALOG_VERSION = '2026.09.2';

/**
 * How big each collection was when {@link CATALOG_VERSION} was last set.
 *
 * This is a mirror of `collection-counts.json`, and the catalog suite fails
 * when the two disagree. It exists because the version rotted badly once and
 * the old guard could not have caught it: the test pinned the version string
 * exactly, so it failed when you changed the version and forgot the test, and
 * never when you changed the data and forgot the version. It only caught the
 * mistake nobody makes.
 *
 * What actually happened: the version was set to `2026.08.12` on 31 August and
 * stayed there through sixty-three commits and three weeks of data, while the
 * catalog grew by roughly three hundred malts and four hundred yeasts. Settings
 * and the ingredients page both show this string to a brewer, so for three
 * weeks they named an August dataset that nobody was running.
 *
 * With the mirror here, adding a row fails *this* file until someone opens it,
 * and the thing to do once it is open is bump the patch. That is the README's
 * own rule, and the cost of keeping it is one line.
 *
 * The counts live in their own JSON file rather than here because it is a
 * published package entrypoint: the marketing build reads it twice to quote
 * the catalog's size on the homepage. This is the copy that forces a decision,
 * not the source of truth — and it is written out by hand rather than imported
 * from that JSON, because this module is bundled into the app and the Worker
 * and an import would drag the file along for what is only a comparison.
 */
export const CATALOG_VERSION_CONTENTS: Readonly<Record<CatalogCollection, number>> = {
  fermentables: 1672,
  hops: 225,
  yeasts: 1093,
  miscs: 106,
  equipment: 76,
  styles: 121,
};
